import { playerOrderCollision } from "@/features/scouting/utils/playerOrderCollision";
/** Unified pointer, touch, and keyboard boundary for Pitch ranking and assignment. */
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  type DragEndEvent,
  type SensorDescriptor,
  type SensorOptions,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import React, { memo, useCallback, useState } from "react";

import WatchlistPitchDragOverlayView from "@/features/scouting/components/molecules/WatchlistPitchDragOverlay";
import { portalToBody } from "@/features/scouting/components/molecules/WatchlistOrderDndContext/WatchlistOrderDndContextContainer";
import type { TacticalSlot } from "@/generated/graphql";

/** Metadata registered by a Pitch assignment target. */
export interface WatchlistPitchDropData {
  readonly kind: "pitch-slot";
  readonly tacticalSlot: TacticalSlot | null;
}

/** Metadata registered by any player drag source in the Pitch composition. */
export interface WatchlistPitchPlayerDragData {
  readonly kind: "pitch-player" | "members-player";
  readonly playerId: string;
}

/**
 * Returns a collision-free drag identity for the left members panel.
 * @param playerId - Stable domain player identity.
 */
export const membersPlayerDragId = (playerId: string): string =>
  `members:${playerId}`;

/**
 * Returns a collision-free drag identity for a player rendered on the field.
 * @param playerId - Stable domain player identity.
 */
export const pitchPlayerDragId = (playerId: string): string =>
  `pitch:${playerId}`;

const playerIdFromDrag = (id: string | number, data?: unknown): string => {
  if (data && typeof data === "object" && "playerId" in data) {
    const playerId = (data as { playerId?: unknown }).playerId;
    if (typeof playerId === "string") return playerId;
  }
  const rawId = String(id);
  return rawId.startsWith("members:") || rawId.startsWith("pitch:")
    ? rawId.slice(rawId.indexOf(":") + 1)
    : rawId;
};

/** Props for the unified Pitch drag boundary. */
export interface WatchlistPitchDndContextProps {
  readonly playerIds: readonly string[];
  readonly playerNames: Readonly<Record<string, string>>;
  /**
   * Each player's current tactical slot (null = Unassigned), read only by the
   * completed-move announcement (SE-8415). Absent → every player reads as
   * Unassigned there; no gesture routing depends on it.
   */
  readonly playerSlots?: Readonly<Record<string, TacticalSlot | null>>;
  readonly disabled?: boolean;
  readonly onReorder: (orderedPlayerIds: readonly string[]) => void;
  readonly onAssign: (
    playerId: string,
    tacticalSlot: TacticalSlot | null
  ) => void;
  readonly children: React.ReactNode;
}

/** A read-only boundary registers no sensors, so a VIEWER cannot lift a player. */
const NO_SENSORS: SensorDescriptor<SensorOptions>[] = [];

const isPitchDropData = (value: unknown): value is WatchlistPitchDropData => {
  if (!value || typeof value !== "object") return false;
  return (value as { kind?: unknown }).kind === "pitch-slot";
};

/**
 * Routes one gesture to ranking or tactical assignment according to its target.
 * @param props - Visible players, permissions, and write callbacks.
 * @param props.playerIds - Player identities in the current ranking order.
 * @param props.playerNames - Accessible player labels keyed by identity.
 * @param props.playerSlots - Each player's current slot, for the announcement.
 * @param props.disabled - Whether all drag writes are disabled.
 * @param props.onReorder - Handles a player-to-player ranking drop.
 * @param props.onAssign - Handles a player-to-slot assignment drop.
 * @param props.children - Pitch panels rendered inside the drag boundary.
 */
const WatchlistPitchDndContextContainer = ({
  playerIds,
  playerNames,
  playerSlots,
  disabled = false,
  onReorder,
  onAssign,
  children,
}: WatchlistPitchDndContextProps) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  const playerName = useCallback(
    (playerId: string | number) =>
      playerNames[playerIdFromDrag(playerId)] ?? playerIdFromDrag(playerId),
    [playerNames]
  );
  const handleDragStart = useCallback(
    ({ active }: DragEndEvent) =>
      setActiveId(playerIdFromDrag(active.id, active.data?.current)),
    []
  );
  const handleDragCancel = useCallback(() => setActiveId(null), []);
  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      setActiveId(null);
      if (disabled || !over) return;
      const playerId = playerIdFromDrag(active.id, active.data?.current);
      if (!playerIds.includes(playerId)) return;

      const dropData = over.data?.current;
      if (isPitchDropData(dropData)) {
        onAssign(playerId, dropData.tacticalSlot);
        return;
      }

      if (active.id === over.id) return;
      const ids: readonly string[] =
        active.data?.current?.groupIds ?? playerIds;
      const from = ids.indexOf(playerId);
      const to = ids.indexOf(playerIdFromDrag(over.id, over.data?.current));
      if (from < 0 || to < 0) return;
      onReorder(arrayMove([...ids], from, to));
    },
    [disabled, onAssign, onReorder, playerIds]
  );
  // SE-8415: the live region reads "{player} moved to {position}, rank {n}"
  // once a move completes — a slot drop changes the position and keeps the
  // rank; a list drop changes the rank and keeps the position.
  const announceMoveEnd = useCallback(
    ({ active, over }: Pick<DragEndEvent, "active" | "over">) => {
      const name = playerName(active.id);
      if (!over) return `Move cancelled for player ${name}.`;
      const playerId = playerIdFromDrag(active.id, active.data?.current);
      const rankOf = (index: number) => (index < 0 ? 1 : index + 1);
      const dropData = over.data?.current;
      const currentSlot = playerSlots?.[playerId] ?? null;
      const ids: readonly string[] =
        active.data?.current?.groupIds ?? playerIds;
      const targetIndex = ids.indexOf(
        playerIdFromDrag(over.id, over.data?.current)
      );
      if (!isPitchDropData(dropData) && targetIndex < 0)
        return `Move cancelled for player ${name}.`;
      const [slot, index] = isPitchDropData(dropData)
        ? [dropData.tacticalSlot, playerIds.indexOf(playerId)]
        : [currentSlot, targetIndex];
      return `${name} moved to ${slot ?? "Unassigned"}, rank ${rankOf(index)}`;
    },
    [playerIds, playerName, playerSlots]
  );

  return (
    <DndContext
      sensors={disabled ? NO_SENSORS : sensors}
      collisionDetection={playerOrderCollision}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
      onDragCancel={handleDragCancel}
      accessibility={{
        screenReaderInstructions: {
          draggable:
            "Press space to lift a player. Use arrow keys to choose a rank or pitch position, press space to drop, or escape to cancel.",
        },
        announcements: {
          onDragStart: ({ active }) =>
            `Lifted player ${playerName(active.id)}.`,
          onDragOver: ({ active, over }) => {
            if (!over)
              return `Player ${playerName(active.id)} is no longer over a destination.`;
            const data = over.data?.current;
            if (isPitchDropData(data)) {
              return data.tacticalSlot
                ? `Player ${playerName(active.id)} moved over position ${data.tacticalSlot}.`
                : `Player ${playerName(active.id)} moved over Unassigned.`;
            }
            return `Player ${playerName(active.id)} moved over ${playerName(over.id)}.`;
          },
          onDragEnd: announceMoveEnd,
          onDragCancel: ({ active }) =>
            `Move cancelled for player ${playerName(active.id)}.`,
        },
      }}
    >
      <SortableContext
        items={playerIds.map(membersPlayerDragId)}
        strategy={verticalListSortingStrategy}
      >
        {children}
      </SortableContext>
      {/* SE-8526: at <body> on web, so the fixed overlay tracks the pointer
          instead of the scrolled panel's transformed ancestor. */}
      {portalToBody(
        <DragOverlay>
          {activeId ? (
            <WatchlistPitchDragOverlayView playerName={playerName(activeId)} />
          ) : null}
        </DragOverlay>
      )}
    </DndContext>
  );
};

WatchlistPitchDndContextContainer.displayName =
  "WatchlistPitchDndContextContainer";
export default memo(WatchlistPitchDndContextContainer);
